import { LOCALE, ZEITZONE } from './constants.js';

/**
 * Deutsche Formatierung — identisch im Browser und in der PDF-Lambda.
 *
 * Datumsteile werden bewusst aus `formatToParts` zusammengesetzt statt über
 * ein Locale-Muster. So hängt das Ergebnis nicht davon ab, wie eine bestimmte
 * ICU-Version das Muster `de-DE` gerade schreibt (CLAUDE.md §3.5).
 */

type Datumsartig = string | number | Date | null | undefined;

const alsDate = (wert: Datumsartig): Date | null => {
  if (wert === null || wert === undefined || wert === '') return null;
  const datum = wert instanceof Date ? wert : new Date(wert);
  return Number.isNaN(datum.getTime()) ? null : datum;
};

const teile = (datum: Date): Record<string, string> => {
  const formatter = new Intl.DateTimeFormat(LOCALE, {
    timeZone: ZEITZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const ergebnis: Record<string, string> = {};
  for (const teil of formatter.formatToParts(datum)) {
    if (teil.type !== 'literal') ergebnis[teil.type] = teil.value;
  }
  // Mitternacht liefert je nach ICU-Version "24" statt "00".
  if (ergebnis.hour === '24') ergebnis.hour = '00';
  return ergebnis;
};

/** `dd.MM.yyyy` in Europe/Berlin. Leerer String, wenn kein Datum vorliegt. */
export const formatiereDatum = (wert: Datumsartig): string => {
  const datum = alsDate(wert);
  if (!datum) return '';
  const t = teile(datum);
  return `${t.day}.${t.month}.${t.year}`;
};

/** `HH:mm` in Europe/Berlin. */
export const formatiereUhrzeit = (wert: Datumsartig): string => {
  const datum = alsDate(wert);
  if (!datum) return '';
  const t = teile(datum);
  return `${t.hour}:${t.minute}`;
};

/** `dd.MM.yyyy, HH:mm` in Europe/Berlin. */
export const formatiereDatumZeit = (wert: Datumsartig): string => {
  const datum = alsDate(wert);
  if (!datum) return '';
  const t = teile(datum);
  return `${t.day}.${t.month}.${t.year}, ${t.hour}:${t.minute}`;
};

/** `dd.MM.yyyy, HH:mm:ss` — für Signatur- und Audit-Zeitstempel. */
export const formatiereZeitstempel = (wert: Datumsartig): string => {
  const datum = alsDate(wert);
  if (!datum) return '';
  const t = teile(datum);
  return `${t.day}.${t.month}.${t.year}, ${t.hour}:${t.minute}:${t.second} Uhr`;
};

/** `yyyy-MM-dd` in Europe/Berlin — für Datumsfelder und Aufbewahrungsfristen. */
export const alsIsoDatum = (wert: Datumsartig): string => {
  const datum = alsDate(wert);
  if (!datum) return '';
  const t = teile(datum);
  return `${t.year}-${t.month}-${t.day}`;
};

/** Zahl mit Komma als Dezimaltrennzeichen und Punkt als Tausenderzeichen. */
export const formatiereZahl = (
  wert: number | string | null | undefined,
  dezimalstellen = 2,
): string => {
  if (wert === null || wert === undefined || wert === '') return '';
  const zahl = typeof wert === 'number' ? wert : parseDeutscheZahl(wert);
  if (zahl === null || !Number.isFinite(zahl)) return '';
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: dezimalstellen,
    maximumFractionDigits: dezimalstellen,
  }).format(zahl);
};

/** Euro-Betrag, z. B. `1.234,50 €`. */
export const formatiereWaehrung = (
  wert: number | string | null | undefined,
  waehrung = 'EUR',
): string => {
  if (wert === null || wert === undefined || wert === '') return '';
  const zahl = typeof wert === 'number' ? wert : parseDeutscheZahl(wert);
  if (zahl === null || !Number.isFinite(zahl)) return '';
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: waehrung,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(zahl);
};

/** Flächenangabe mit Einheit, z. B. `24,50 m²`. */
export const formatiereFlaeche = (wert: number | null | undefined): string => {
  if (wert === null || wert === undefined) return '';
  return `${formatiereZahl(wert, 2)} m²`;
};

export const formatiereJaNein = (wert: unknown): string => {
  if (wert === null || wert === undefined || wert === '') return '—';
  if (typeof wert === 'string') {
    const normal = wert.trim().toLowerCase();
    if (['ja', 'true', '1', 'yes'].includes(normal)) return 'Ja';
    if (['nein', 'false', '0', 'no'].includes(normal)) return 'Nein';
    return wert;
  }
  return wert ? 'Ja' : 'Nein';
};

const EINHEITEN = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

export const formatiereDateigroesse = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  let wert = bytes;
  let index = 0;
  while (wert >= 1024 && index < EINHEITEN.length - 1) {
    wert /= 1024;
    index += 1;
  }
  return `${formatiereZahl(wert, wert >= 100 ? 0 : 1)} ${EINHEITEN[index]}`;
};

/** Liest eine deutsch geschriebene Zahl (`1.234,56`) zurück. */
export const parseDeutscheZahl = (eingabe: string | number | null | undefined): number | null => {
  if (eingabe === null || eingabe === undefined) return null;
  if (typeof eingabe === 'number') return Number.isFinite(eingabe) ? eingabe : null;
  const roh = eingabe.trim();
  if (roh === '') return null;
  // Tausenderpunkte entfernen, Komma zu Punkt.
  const normalisiert = roh
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}\b)/g, '')
    .replace(',', '.');
  const zahl = Number(normalisiert);
  return Number.isFinite(zahl) ? zahl : null;
};

/** Liest `dd.MM.yyyy` (auch `d.M.yy`) und ISO-Datumsangaben. */
export const parseDeutschesDatum = (eingabe: string | null | undefined): Date | null => {
  if (!eingabe) return null;
  const roh = eingabe.trim();
  if (roh === '') return null;

  const deutsch = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/.exec(roh);
  if (deutsch) {
    const tag = Number(deutsch[1]);
    const monat = Number(deutsch[2]);
    let jahr = Number(deutsch[3]);
    if (jahr < 100) jahr += jahr < 70 ? 2000 : 1900;
    const datum = new Date(Date.UTC(jahr, monat - 1, tag, 12, 0, 0));
    if (
      datum.getUTCFullYear() !== jahr ||
      datum.getUTCMonth() !== monat - 1 ||
      datum.getUTCDate() !== tag
    ) {
      return null;
    }
    return datum;
  }

  const iso = alsDate(roh);
  return iso;
};

/**
 * Erzeugt aus einem lokalen Datums-/Zeitwert einen ISO-8601-UTC-String —
 * gespeichert wird ausschließlich UTC (CLAUDE.md §5).
 */
export const alsIsoUtc = (wert: Datumsartig): string | null => {
  const datum = alsDate(wert);
  return datum ? datum.toISOString() : null;
};

/** Kürzt einen Text und hängt ein Auslassungszeichen an. */
export const kuerze = (text: string, maxLaenge: number): string =>
  text.length <= maxLaenge ? text : `${text.slice(0, Math.max(0, maxLaenge - 1))}…`;
